import { useNavigate } from "react-router-dom";
import MainContainer from "../../../layout/MainContainer";
import UserListFilter from "../../../features/userManagement/userFilter";
import UserListTable from "../../../features/userManagement/userListTable";
import { useEffect, useState } from "react";

import {
  useAddSubadminMutation,
  useChangeSubadminStatusMutation,
  useGetAllSubadminsMutation,
  useLazyDeleteSubadminQuery,
  useLazyGetAllRolesQuery,
  useLazySubadminDetailQuery,
  useUpdateSubadminMutation,
} from "../../../service/subadmin";
import { UploadMedia } from "../../../utils/mediaUpload";
import { UPDATE_TYPES, USER_ROLES } from "../../../utils/enums";
import { generateEncryptedKeyBody } from "../../../utils/crypto";
import { CommonBody, RoleTypes, SubadminTypes } from "../../../types/General";
import { errorToast, successToast } from "../../../helpers";
import AddUser from "../../../Modals/addUser";
import { ConfirmationModal } from "../../../Modals/confirmationModal";
import { useExportCsvMutation } from "../../../service/users";

const UserList = () => {
  const navigate = useNavigate();
  const [flag, setFlag] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openWarnModal, setOpenWarnModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [profile_picture_id, setProfile_picture_id] = useState<number | null>(
    null
  );

  const [roles, setRoles] = useState<RoleTypes[]>([]);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [selectedFilterRole, setSelectedFilterRole] = useState<number | null>(
    null
  );

  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("+971");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState<SubadminTypes[]>([]);
  const [page, setPage] = useState<number>(1);

  const [getSubadmins] = useGetAllSubadminsMutation();
  const [addSubadmin] = useAddSubadminMutation();
  const [updateSubadmin] = useUpdateSubadminMutation();
  const [deleteSubadmin] = useLazyDeleteSubadminQuery();
  const [changeStatus] = useChangeSubadminStatusMutation();
  const [subadmiDetail] = useLazySubadminDetailQuery();
  const [getRoles] = useLazyGetAllRolesQuery();
  const [exportCsv] = useExportCsvMutation();

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ""));
    setDialCode(country?.dialCode);
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleCloseModal = () => {
    setError(false);
    setSelectedId(null);
    setProfileImage("");
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setPhone("");
    setDialCode("+971");
    setEmail("");
    setProfile_picture_id(null);
    setSelectedRole(null);
    setOpenModal(false);
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }
    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      errorToast("Please upload the image in the JPG, JPEG or PNG format");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.[0]?.media_url) {
      setProfileImage(imageUrl?.data?.[0]?.media_url);
      setProfile_picture_id(imageUrl?.data?.[0]?.id);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await getRoles().unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRoles(response?.data || []);
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const body = {
        role: USER_ROLES.SUBADMIN,
        ...(selectedFilterRole ? { sub_role: selectedFilterRole } : {}),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await exportCsv(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        window.open(response?.file_urls);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const fetchSubadmins = async () => {
    const body = {
      start: page,
      length: 10,
      search: debouncedSearchTerm.trim(),
      ...(selectedFilterRole ? { role: selectedFilterRole } : {}),
    };

    setLoading(true);

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getSubadmins(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };
  const handleSubmit = async () => {
    setError(true);
    if (!firstName?.length || firstName?.length <= 2) {
      return;
    }
    if (!email?.length) {
      return;
    }
    if (phone?.length <= 5) {
      return;
    }
    if (!selectedRole) {
      errorToast("Please add a sub-admin role before creating a sub-admin.");
      return;
    }
    const body = {
      phone_no: phone,
      email,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      ...(profile_picture_id
        ? { profile_picture: Number(profile_picture_id) }
        : {}),
      country_code: dialCode?.includes("+") ? dialCode : "+" + dialCode,
      user_role_type: USER_ROLES.SUBADMIN,
      role: selectedRole,
    };

    try {
      setLoading(true);
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;
      if (selectedId) {
        response = await updateSubadmin({
          body: encryptedBody,
          id: selectedId,
        }).unwrap();
      } else {
        response = await addSubadmin(encryptedBody).unwrap();
      }
      setLoading(false);

      if (response?.status === 200) {
        if (selectedId) {
          successToast("Subadmin details updated successfully");
        } else {
          successToast("Subadmin details added successfully");
        }
        handleCloseModal();
        fetchSubadmins();
      }
    } catch (error: any) {
      setLoading(false);
      errorToast(error?.data?.message || "");
    }
  };
  const handleUserUpdate = async (id: number, status: any, type: string) => {
    const body = {
      ...(type === UPDATE_TYPES.BLOCK ? { is_active: status } : {}),
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await changeStatus({
        body: encryptedBody,
        id: id,
      }).unwrap();
      if (response?.status === 200) {
        const updatedData = rows?.map((item) => {
          if (item?.id === id) {
            return {
              ...item,
              is_active: status,
            };
          }
          return item;
        });
        setRows(updatedData);
        if (status === true) {
          successToast("Status enabled successfully");
        } else {
          successToast("Status disabled successfully");
        }
      } else {
        errorToast("Please try again");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const fetchSubamdinDetails = async (selectedRoleId: number) => {
    setLoading(true);
    try {
      const response = await subadmiDetail({ id: selectedRoleId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setFirstName(response?.data?.first_name || "");
        setMiddleName(response?.data?.middle_name || "");
        setLastName(response?.data?.last_name || "");
        setDialCode(response?.data?.country_code || "+971");
        setPhone(response?.data?.phone_no || "");
        setProfileImage(response?.data?.profile_picture?.media_url || "");
        setProfile_picture_id(response?.data?.profile_picture?.id || null);
        setSelectedRole(response?.data?.sub_role_id || null);
        setEmail(response?.data?.email || "");
      } else {
        setRoles([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteSubadmin({ id: selectedId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Sub-admin deleted successfully");
        fetchSubadmins();
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedId && flag) {
      fetchSubamdinDetails(selectedId);
    }
  }, [selectedId, openModal]);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchSubadmins();
  }, [debouncedSearchTerm, page, selectedFilterRole]);

  return (
    <>
      <MainContainer>
        <div className="main_title">
          <h1>Sub-admin List</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> - User
            Management - Sub-admin List
          </p>
        </div>

        <div className="cards">
          <UserListFilter
            setOpenModal={setOpenModal}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            roles={roles}
            selectedRole={selectedFilterRole}
            setSelectedRole={setSelectedFilterRole}
            handleExportCSV={handleExportCSV}
            rows={rows}
          />
          <UserListTable
            rows={rows}
            loading={loading}
            count={count}
            page={page}
            handlePageChange={handlePageChange}
            setSelectedId={setSelectedId}
            open={openModal}
            setOpen={setOpenModal}
            setOpenWarnModal={setOpenWarnModal}
            handleUserUpdate={handleUserUpdate}
            setFlag={setFlag}
          />
        </div>
        <AddUser
          open={openModal}
          onClose={handleCloseModal}
          setOpen={setOpenModal}
          handleSubmit={handleSubmit}
          profileImage={profileImage}
          setProfileImage={setProfileImage}
          handleProfileUpload={handleProfileUpload}
          firstName={firstName}
          setFirstName={setFirstName}
          error={error}
          middleName={middleName}
          setMiddleName={setMiddleName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          phone={phone}
          dialCode={dialCode}
          handleChangePhone={handleChangePhone}
          setError={setError}
          roles={roles}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          selectedId={selectedId}
        />
      </MainContainer>
      <ConfirmationModal
        text={"Are you sure to delete this sub-admin?"}
        openModal={openWarnModal}
        setOpenModal={setOpenWarnModal}
        handleSubmit={handleDelete}
      />
    </>
  );
};

export default UserList;
